// src/pages/lab/processing/processingtabs/components/stepExecution/RegularStepView.tsx
//
// The working surface for a non-result step: what to do (methods), what it
// needs (materials, equipment), what to record (data collection) and what the
// system did on its own (actions).

import React, { useState } from "react";
import { Checkbox, DatePicker, Input, InputNumber, Select, Tabs, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import { DataTable, EmptyState, StatusTag } from "../../../../../../components/shared";

// Types
import type {
	AnalysisProtocolInstance,
	GroupStepActionLog,
	GroupStepInstance,
	ProcessingGroup,
	ProtocolStep,
	ProtocolStepMaterial,
	SampleProtocolInstance,
	StepActionLog,
	StepField,
	StepInstanceData,
} from "../../../../../../types/lab/ProtocolTypes";

const { Text } = Typography;
const { Option } = Select;

interface RegularStepViewProps {
	group?: ProcessingGroup;
	step: ProtocolStep;
	stepData?: StepInstanceData | GroupStepInstance;
	fieldValues: Record<string, any>;
	onFieldValueChange: (fieldName: string, value: any) => void;
	/**
	 * Per-instance field values entered in the per-sample grid.
	 * Key: instance ID (string). Value: field_values map for that instance.
	 */
	perSampleFieldValues?: Record<string, Record<string, any>>;
	onPerSampleFieldValuesChange?: (values: Record<string, Record<string, any>>) => void;
	actionLogs: StepActionLog[] | GroupStepActionLog[];
	readOnly?: boolean;
}

/** Turns a snake_case field name into a readable label. */
function fieldLabel(name: string): string {
	return name.replace(/_/g, " ");
}

const TAB_BODY: React.CSSProperties = { padding: "12px 16px" };

const FieldLabel: React.FC<{ name: string; required?: boolean }> = ({ name, required }) => (
	<div style={{ fontSize: 10, color: "var(--mg-text-secondary)", marginBottom: 3 }}>
		{fieldLabel(name)}
		{required && <Text type="danger"> *</Text>}
	</div>
);

const RegularStepView: React.FC<RegularStepViewProps> = ({
	group,
	step,
	stepData,
	fieldValues,
	onFieldValueChange,
	perSampleFieldValues = {},
	onPerSampleFieldValuesChange,
	actionLogs,
	readOnly = false,
}) => {
	const [activeTab, setActiveTab] = useState("1");

	const renderFieldInput = (field: StepField): JSX.Element => {
		const value = fieldValues[field.field_name];

		switch (field.field_type) {
			case "number":
				return (
					<InputNumber
						value={value || null}
						onChange={(val) => onFieldValueChange(field.field_name, val)}
						placeholder={fieldLabel(field.field_name)}
						style={{ width: "100%" }}
						min={field.validation_rules?.min}
						max={field.validation_rules?.max}
						size="small"
						disabled={readOnly}
					/>
				);

			case "dropdown": {
				const options = field.validation_rules?.in || [];
				const isMulti = field.validation_rules?.multi_select === true;
				return (
					<Select
						mode={isMulti ? "multiple" : undefined}
						value={isMulti ? (Array.isArray(value) ? value : []) : value || undefined}
						onChange={(val) => onFieldValueChange(field.field_name, val)}
						placeholder={`Select ${fieldLabel(field.field_name)}`}
						style={{ width: "100%" }}
						size="small"
						disabled={readOnly}
					>
						{options.map((opt) => (
							<Option key={opt} value={opt}>
								{opt}
							</Option>
						))}
					</Select>
				);
			}

			case "boolean":
				return (
					<Checkbox
						checked={value || false}
						onChange={(e) => onFieldValueChange(field.field_name, e.target.checked)}
						disabled={readOnly}
					>
						<Text style={{ fontSize: 12 }}>{fieldLabel(field.field_name)}</Text>
					</Checkbox>
				);

			// Saved values come back from the API as ISO strings, and the picker needs a dayjs.
			case "date":
				return (
					<DatePicker
						value={value ? dayjs(value) : null}
						onChange={(date) => onFieldValueChange(field.field_name, date)}
						style={{ width: "100%" }}
						size="small"
						disabled={readOnly}
					/>
				);

			case "datetime":
				return (
					<DatePicker
						showTime
						value={value ? dayjs(value) : null}
						onChange={(date) => onFieldValueChange(field.field_name, date)}
						style={{ width: "100%" }}
						size="small"
						disabled={readOnly}
					/>
				);

			default:
				return (
					<Input
						value={value || ""}
						onChange={(e) => onFieldValueChange(field.field_name, e.target.value)}
						placeholder={fieldLabel(field.field_name)}
						maxLength={field.validation_rules?.max_length}
						size="small"
						disabled={readOnly}
					/>
				);
		}
	};

	// ── Methods — an ordered bench procedure, so the numbering is real ────────
	const renderMethods = (): JSX.Element => {
		const methods = step.method || [];
		if (methods.length === 0) {
			return (
				<EmptyState
					title="No method steps"
					description="This step has no written procedure attached to it."
				/>
			);
		}

		const sortedMethods = [...methods].sort((a, b) => a.step_order_number - b.step_order_number);

		return (
			<ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
				{sortedMethods.map((method, index) => (
					<li
						key={method.step_order_number}
						style={{
							display: "flex",
							gap: 10,
							padding: "8px 0",
							borderTop: index === 0 ? "none" : "1px solid var(--mg-border)",
						}}
					>
						<span
							style={{
								flexShrink: 0,
								width: 20,
								fontSize: 11,
								fontWeight: 600,
								fontVariantNumeric: "tabular-nums",
								color: "var(--mg-text-secondary)",
								lineHeight: "18px",
							}}
						>
							{method.step_order_number}
						</span>
						<div style={{ minWidth: 0 }}>
							<div style={{ fontSize: 12, fontWeight: 500, color: "var(--mg-text)" }}>
								{method.name}
							</div>
							{method.description && (
								<Text type="secondary" style={{ fontSize: 11, lineHeight: 1.6 }}>
									{method.description}
								</Text>
							)}
						</div>
					</li>
				))}
			</ol>
		);
	};

	const renderMaterials = (): JSX.Element => {
		const materials = step.materials || [];
		if (materials.length === 0) {
			return (
				<EmptyState title="No materials" description="This step consumes no listed materials." />
			);
		}

		const columns: ColumnsType<ProtocolStepMaterial> = [
			{
				title: "Material",
				dataIndex: "name",
				key: "name",
				render: (_: any, record: ProtocolStepMaterial) => (
					<div>
						<div style={{ fontSize: 12, fontWeight: 500 }}>{record.name}</div>
						{record.description && (
							<Text type="secondary" style={{ fontSize: 11 }}>
								{record.description}
							</Text>
						)}
					</div>
				),
			},
			{
				title: "Per sample",
				dataIndex: "quantity",
				key: "quantity",
				width: 110,
				align: "right",
				render: (qty: number) => <span className="mg-mono-num">{qty}</span>,
			},
			{
				title: "Group total",
				key: "total",
				width: 110,
				align: "right",
				render: (_: any, record: ProtocolStepMaterial) => (
					<span className="mg-mono-num" style={{ fontWeight: 600 }}>
						{parseInt(record.quantity.toString(), 10) * (group?.sample_count || 0)}
					</span>
				),
			},
		];

		return (
			<DataTable
				bare
				columns={columns}
				dataSource={materials}
				rowKey="name"
				pagination={false}
				scrollX="max-content"
			/>
		);
	};

	const renderDataFields = (): JSX.Element => {
		const fields = step.configuration?.required_fields || [];
		if (fields.length === 0) {
			return (
				<EmptyState
					title="Nothing to record"
					description="This step collects no data — complete it once the bench work is done."
				/>
			);
		}

		// ── Per-sample grid ──────────────────────────────────────────────────────
		// When the group has applicable instance IDs, show an editable table so the
		// technician can enter different values per sample/analysis.
		const gsi = stepData as GroupStepInstance | undefined;

		// Use || (not ??) so an empty sample_instances array falls through to analysis_instances
		const isAnalysisGroup = (group?.analysis_instances?.length ?? 0) > 0;
		// Widened to the union so the two instance shapes share one array type —
		// `.filter` is not callable across a `A[] | B[]` union.
		const instances: Array<SampleProtocolInstance | AnalysisProtocolInstance> = isAnalysisGroup
			? group?.analysis_instances ?? []
			: group?.sample_instances ?? [];
		const instanceLabel = isAnalysisGroup ? "Analysis ID" : "Sample ID";

		const applicableInstances = gsi?.applicable_instance_ids
			? instances.filter((i: any) => (gsi!.applicable_instance_ids as number[]).includes(i.id))
			: instances;

		const hasMultipleInstances = applicableInstances.length > 1;

		const updatePerSampleField = (instanceId: number, fieldName: string, value: any) => {
			if (!onPerSampleFieldValuesChange) return;
			const updated = {
				...perSampleFieldValues,
				[String(instanceId)]: {
					...(perSampleFieldValues[String(instanceId)] ?? {}),
					[fieldName]: value,
				},
			};
			onPerSampleFieldValuesChange(updated);
		};

		const renderCellInput = (field: StepField, instanceId: number): JSX.Element => {
			const instanceValues = perSampleFieldValues[String(instanceId)] ?? {};
			const val = instanceValues[field.field_name];

			switch (field.field_type) {
				case "number":
					return (
						<InputNumber
							value={val ?? null}
							onChange={(v) => updatePerSampleField(instanceId, field.field_name, v)}
							placeholder="—"
							style={{ width: "100%" }}
							min={field.validation_rules?.min}
							max={field.validation_rules?.max}
							size="small"
							disabled={readOnly}
						/>
					);
				case "dropdown": {
					const cellIsMulti = field.validation_rules?.multi_select === true;
					return (
						<Select
							mode={cellIsMulti ? "multiple" : undefined}
							value={cellIsMulti ? (Array.isArray(val) ? val : []) : val}
							onChange={(v) => updatePerSampleField(instanceId, field.field_name, v)}
							placeholder="—"
							style={{ width: "100%" }}
							size="small"
							disabled={readOnly}
						>
							{(field.validation_rules?.in ?? []).map((opt) => (
								<Option key={opt} value={opt}>
									{opt}
								</Option>
							))}
						</Select>
					);
				}
				case "boolean":
					return (
						<Checkbox
							checked={!!val}
							onChange={(e) => updatePerSampleField(instanceId, field.field_name, e.target.checked)}
							disabled={readOnly}
						/>
					);
				default:
					return (
						<Input
							value={val ?? ""}
							onChange={(e) => updatePerSampleField(instanceId, field.field_name, e.target.value)}
							placeholder="—"
							size="small"
							disabled={readOnly}
						/>
					);
			}
		};

		const cellStyle: React.CSSProperties = {
			padding: "4px 10px",
			borderBottom: "1px solid var(--mg-border)",
		};

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				{/* Group-level fields — apply identical values to all samples */}
				<div>
					<div
						style={{
							fontSize: 10,
							fontWeight: 600,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "var(--mg-text-secondary)",
							marginBottom: 6,
						}}
					>
						Group values
					</div>
					<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
						Applied to all {isAnalysisGroup ? "analyses" : "samples"} unless overridden below.
					</Text>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
							gap: 12,
						}}
					>
						{fields.map((field, index) => (
							<div key={index}>
								<FieldLabel name={field.field_name} required={field.required} />
								{renderFieldInput(field)}
							</div>
						))}
					</div>
				</div>

				{/* Per-sample grid — visible when group has multiple applicable instances */}
				{hasMultipleInstances && onPerSampleFieldValuesChange && (
					<div>
						<div
							style={{
								fontSize: 10,
								fontWeight: 600,
								textTransform: "uppercase",
								letterSpacing: "0.06em",
								color: "var(--mg-text-secondary)",
								marginBottom: 6,
							}}
						>
							Per {isAnalysisGroup ? "analysis" : "sample"} values
						</div>
						<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
							Leave a cell blank to use the group value.
						</Text>
						<div style={{ overflowX: "auto", border: "1px solid var(--mg-border)", borderRadius: "var(--mg-radius-sm)" }}>
							<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
								<thead>
									<tr style={{ background: "var(--mg-bg-subtle)" }}>
										<th
											style={{
												...cellStyle,
												textAlign: "left",
												fontSize: 10,
												fontWeight: 600,
												textTransform: "uppercase",
												letterSpacing: "0.04em",
												color: "var(--mg-text-secondary)",
												whiteSpace: "nowrap",
											}}
										>
											{instanceLabel}
										</th>
										{fields.map((f) => (
											<th
												key={f.field_name}
												style={{
													...cellStyle,
													textAlign: "left",
													fontSize: 10,
													fontWeight: 600,
													textTransform: "uppercase",
													letterSpacing: "0.04em",
													color: "var(--mg-text-secondary)",
													whiteSpace: "nowrap",
												}}
											>
												{fieldLabel(f.field_name)}
												{f.required && <Text type="danger"> *</Text>}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{applicableInstances.map((instance: any) => {
										const rowLabel = instance.sample_id
											? instance.sample_id
											: instance.analysis_id
											? `#${instance.analysis_id}`
											: `#${instance.id}`;
										return (
											<tr key={instance.id}>
												<td
													style={{
														...cellStyle,
														whiteSpace: "nowrap",
														fontWeight: 500,
														fontVariantNumeric: "tabular-nums",
													}}
												>
													{rowLabel}
												</td>
												{fields.map((field) => (
													<td key={field.field_name} style={{ ...cellStyle, minWidth: 140 }}>
														{renderCellInput(field, instance.id)}
													</td>
												))}
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderActionLogs = (): JSX.Element => {
		if (actionLogs.length === 0) {
			return (
				<EmptyState
					title="No automatic actions"
					description="Nothing has run automatically for this step yet."
				/>
			);
		}

		return (
			<div>
				{actionLogs.map((log, index) => (
					<div
						key={log.id}
						style={{
							display: "flex",
							alignItems: "flex-start",
							justifyContent: "space-between",
							gap: 12,
							padding: "8px 0",
							borderTop: index === 0 ? "none" : "1px solid var(--mg-border)",
						}}
					>
						<div style={{ minWidth: 0 }}>
							<div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
								<span style={{ fontSize: 12, fontWeight: 500 }}>
									{log.action_type.replace(/_/g, " ")}
								</span>
								<StatusTag status={log.status} size="small" />
							</div>
							{log.error_message && (
								<Text type="danger" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
									{log.error_message}
								</Text>
							)}
						</div>
						<Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
							{log.executed_at ? new Date(log.executed_at).toLocaleString() : "—"}
						</Text>
					</div>
				))}
			</div>
		);
	};

	const renderEquipment = (): JSX.Element => {
		const equipment = step.configuration?.equipment_requirements || [];
		if (equipment.length === 0) {
			return (
				<EmptyState title="No equipment" description="This step needs no specific instrument." />
			);
		}

		const columns: ColumnsType<any> = [
			{
				title: "Equipment",
				dataIndex: "name",
				key: "name",
				render: (text: string) => <span style={{ fontSize: 12, fontWeight: 500 }}>{text}</span>,
			},
			{
				title: "Type",
				dataIndex: "type",
				key: "type",
				render: (text: string) => (
					<Text type="secondary" style={{ fontSize: 12 }}>
						{text}
					</Text>
				),
			},
		];

		return (
			<DataTable
				bare
				columns={columns}
				dataSource={equipment}
				rowKey="name"
				pagination={false}
				scrollX="max-content"
			/>
		);
	};

	const fieldCount = step.configuration?.required_fields?.length ?? 0;
	const methodCount = step.method?.length ?? 0;
	const materialCount = step.materials?.length ?? 0;
	const equipmentCount = step.configuration?.equipment_requirements?.length ?? 0;

	/** Tab label with its count inline — the count is the useful part. */
	const tabLabel = (label: string, count: number) => (
		<span>
			{label}
			{count > 0 && (
				<span style={{ marginLeft: 5, fontSize: 10, color: "var(--mg-text-secondary)" }}>{count}</span>
			)}
		</span>
	);

	return (
		<Tabs
			size="small"
			activeKey={activeTab}
			onChange={setActiveTab}
			tabBarStyle={{ margin: 0, padding: "0 16px" }}
			items={[
				{
					key: "1",
					label: tabLabel("Data", fieldCount),
					children: <div style={TAB_BODY}>{renderDataFields()}</div>,
				},
				{
					key: "2",
					label: tabLabel("Methods", methodCount),
					children: <div style={TAB_BODY}>{renderMethods()}</div>,
				},
				{
					key: "3",
					label: tabLabel("Materials", materialCount),
					children: <div style={TAB_BODY}>{renderMaterials()}</div>,
				},
				{
					key: "4",
					label: tabLabel("Equipment", equipmentCount),
					children: <div style={TAB_BODY}>{renderEquipment()}</div>,
				},
				{
					key: "5",
					label: tabLabel("Actions", actionLogs.length),
					children: <div style={TAB_BODY}>{renderActionLogs()}</div>,
				},
			]}
		/>
	);
};

export default RegularStepView;
